/**
 * Restaurant module-details request/response shapes.
 * Backend enum is `RESTAURANT`, but the URL/folder use `restaurant`.
 * Mirrors backend/src/modules/restaurant/restaurant.validators.ts.
 */

export type RestaurantType = 'PET_RESTAURANT' | 'PET_CAFE' | 'PET_BAKERY';
export type DeliveryRadius = 'KM_3' | 'KM_5' | 'KM_10' | 'KM_15_PLUS';
export type RestaurantService = 'DINE_IN' | 'HOME_DELIVERY' | 'PICKUP' | 'PET_PARTY';
export type PetSpecies = 'DOG' | 'CAT' | 'BIRD' | 'RABBIT' | 'FISH' | 'OTHER';

export type RestaurantDetailsRequest = {
  restaurant_type?: RestaurantType[];
  delivery_radius?: DeliveryRadius;
  fssai_license_number?: string; // 14-digit regex
  pet_friendly_species: PetSpecies[]; // 1..6 unique
  services_offered: RestaurantService[]; // 1..4 unique
  opening_time: string; // HH:mm, < closing_time
  closing_time: string; // HH:mm
};

export type RestaurantDetailsResponse = {
  id: string;
  vendor_profile_id: string;
  restaurant_type: RestaurantType[];
  delivery_radius: DeliveryRadius | null;
  fssai_license_number: string | null;
  pet_friendly_species: PetSpecies[];
  services_offered: RestaurantService[];
  opening_time: string;
  closing_time: string;
  created_at: string;
  updated_at: string;
};

export const RESTAURANT_TYPE_OPTIONS: Array<{ value: RestaurantType; label: string }> = [
  { value: 'PET_RESTAURANT', label: 'Pet restaurant' },
  { value: 'PET_CAFE', label: 'Pet café' },
  { value: 'PET_BAKERY', label: 'Pet bakery' },
];

export const DELIVERY_RADIUS_OPTIONS: Array<{ value: DeliveryRadius; label: string }> = [
  { value: 'KM_3', label: '3 km' },
  { value: 'KM_5', label: '5 km' },
  { value: 'KM_10', label: '10 km' },
  { value: 'KM_15_PLUS', label: '15+ km' },
];

export const RESTAURANT_SERVICE_OPTIONS: Array<{
  value: RestaurantService;
  label: string;
}> = [
  { value: 'DINE_IN', label: 'Dine-in' },
  { value: 'HOME_DELIVERY', label: 'Home delivery' },
  { value: 'PICKUP', label: 'Pickup' },
  { value: 'PET_PARTY', label: 'Pet party' },
];

export const PET_SPECIES_OPTIONS: Array<{ value: PetSpecies; label: string }> = [
  { value: 'DOG', label: 'Dog' },
  { value: 'CAT', label: 'Cat' },
  { value: 'BIRD', label: 'Bird' },
  { value: 'RABBIT', label: 'Rabbit' },
  { value: 'FISH', label: 'Fish' },
  { value: 'OTHER', label: 'Other' },
];

export const FSSAI_REGEX = /^\d{14}$/;
